import { meStore } from '@gozoki/api'
import type { StoreCamera, StoreCameraViewSession } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { Camera01Icon } from './icons/dashboard-icons'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { StoreCameraPlayer } from './StoreCameraPlayer'

const withToken = (url: string, token: string): string => {
  const parsed = new URL(url)
  parsed.searchParams.set('token', token)
  return parsed.toString()
}

/** Cadence de rafraîchissement des vignettes, annoncée par le sous-titre de la modale. */
const SNAPSHOT_REFRESH_MS = 60_000
/** Battement de l'horloge : deux fois par minute, assez pour que l'incrustation ne retarde pas. */
const CLOCK_TICK_MS = 30_000
/** Grille plein écran : 3 colonnes × 2 lignes = exactement 6 caméras par page (Figma), sans scroll. */
const CAMERAS_PER_PAGE = 6

/**
 * Contenu d'une tuile caméra, par ordre de préférence : le direct (si la tuile est sélectionnée et
 * jouable), sinon la dernière image connue, sinon un message. `session` n'est passée que si le flux
 * live doit réellement jouer.
 */
function TileView({
  camera,
  session,
  snapshotSrc,
}: {
  camera: StoreCamera
  session: StoreCameraViewSession | undefined
  snapshotSrc: string | null
}) {
  if (session) {
    return (
      <StoreCameraPlayer
        cameraName={camera.name}
        session={session}
        retryNonce={0}
        className="absolute inset-0 rounded-[16px]"
      />
    )
  }

  if (snapshotSrc) {
    return (
      <img
        src={snapshotSrc}
        alt={camera.label}
        className="absolute inset-0 size-full object-cover"
      />
    )
  }

  return (
    <span className="absolute inset-0 flex items-center justify-center font-sans text-[14px] text-white">
      Flux indisponible
    </span>
  )
}

/**
 * Modale « Caméras » (Figma 11393:48967) : la grille de TOUTES les caméras de la boutique, une tuile
 * par caméra (snapshot rafraîchi chaque minute + horodatage). Un clic sur une tuile jouable bascule
 * cette tuile en direct sur place (re-clic pour revenir au snapshot).
 *
 * Rendue DANS le canvas mis à l'échelle et non via le `Dialog` partagé : celui-ci passe par un
 * portail sur `document.body`, donc hors du `transform: scale()` du dashboard — la modale
 * s'afficherait à l'échelle 1 au milieu d'une interface à ~1,5. Un `position: fixed` sous un
 * ancêtre transformé se cale sur cet ancêtre, pas sur le viewport.
 */
export function CamerasModal({
  cameras,
  session,
  initialCameraName,
  onClose,
}: {
  cameras: StoreCamera[]
  session: StoreCameraViewSession | undefined
  initialCameraName: string | null
  onClose: () => void
}) {
  const { data: me } = useQuery({ queryKey: ['store', 'me'], queryFn: meStore })
  // Caméra jouée en direct (une seule à la fois) ; null = toute la grille en snapshot.
  const [liveName, setLiveName] = useState<string | null>(initialCameraName)
  // Pagination : 6 caméras par écran. On ouvre sur la page qui contient la caméra visée (chevron
  // sans caméra → page 0).
  const pageCount = Math.max(1, Math.ceil(cameras.length / CAMERAS_PER_PAGE))
  const [page, setPage] = useState(() => {
    const target = cameras.findIndex((camera) => camera.name === initialCameraName)
    return target !== -1 ? Math.floor(target / CAMERAS_PER_PAGE) : 0
  })
  // Une seule horloge pilote l'incrustation ET le cache-buster des vignettes : arrondie à la
  // minute, elle garantit qu'une vignette n'est rechargée qu'une fois par minute.
  const [now, setNow] = useState(() => DateTime.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), CLOCK_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const snapshotNonce = Math.floor(now.toMillis() / SNAPSHOT_REFRESH_MS)
  const snapshotSrc = (camera: StoreCamera): string | null =>
    camera.snapshotUrl && session
      ? `${withToken(camera.snapshotUrl, session.token)}&_=${snapshotNonce}`
      : null

  const timestamp = now.setZone('Europe/Paris').toFormat('HH:mm - dd.MM.yyyy')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#072C1A]/25 p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label="Caméras de la boutique"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-[1917px] flex-col gap-[16px] overflow-hidden rounded-[24px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête : icône + titre à gauche, bouton fermer à droite. */}
        <div className="flex items-center gap-[24px]">
          <div className="flex min-w-0 flex-1 items-center gap-[12px]">
            <span className="flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#F9F8F1] text-[#4F6A47]">
              <Camera01Icon className="size-[24px]" />
            </span>
            <h2 className="truncate font-brand text-[22px] leading-[26px] font-semibold text-[#4F6A47] uppercase">
              Caméras {me?.store.label ?? ''}
            </h2>
          </div>

          <ModalCloseButton onClose={onClose} ariaLabel="Fermer les caméras" />
        </div>

        <div className="h-px w-full shrink-0 bg-[#0000000F]" />

        {cameras.length > 0 ? (
          // 6 caméras par page (3 colonnes × 2 lignes) qui remplissent l'écran sans scroll. Les
          // lignes se partagent la hauteur disponible ; au-delà de 6 caméras, on pagine.
          <div className="grid min-h-0 flex-1 grid-cols-3 grid-rows-2 gap-[24px]">
            {cameras
              .slice(page * CAMERAS_PER_PAGE, page * CAMERAS_PER_PAGE + CAMERAS_PER_PAGE)
              .map((camera, index) => {
                const globalIndex = page * CAMERAS_PER_PAGE + index
                const isLive = liveName === camera.name && Boolean(camera.fullStreamUrl && session)
                const playable = Boolean(camera.fullStreamUrl && session)
                return (
                  <div key={camera.id} className="flex min-h-0 flex-col items-center gap-[10px]">
                    <p className="w-full shrink-0 truncate text-center font-brand text-[22px] leading-[26px] font-extrabold text-[#6A9560]">
                      {globalIndex + 1} - {camera.label}
                    </p>

                    <button
                      type="button"
                      onClick={() =>
                        setLiveName((current) => (current === camera.name ? null : camera.name))
                      }
                      disabled={!playable}
                      aria-pressed={isLive}
                      aria-label={
                        isLive
                          ? `Arrêter le direct ${camera.label}`
                          : `Voir en direct ${camera.label}`
                      }
                      className={cn(
                        'relative min-h-0 w-full flex-1 overflow-hidden rounded-[16px] bg-[#2F3D28]',
                        playable ? 'cursor-pointer' : 'cursor-default',
                      )}
                    >
                      <TileView
                        camera={camera}
                        session={isLive ? session : undefined}
                        snapshotSrc={snapshotSrc(camera)}
                      />

                      <div className="pointer-events-none absolute inset-0 flex items-start justify-between p-[16px]">
                        {/* Horodatage (Figma 11393:49117) : coin haut-gauche. */}
                        <span className="rounded-[6px] border border-black/[0.06] bg-[rgba(21,32,19,0.6)] px-[8px] py-[4px] font-sans text-[14px] leading-[16px] font-medium text-[#F9F8F1]">
                          {timestamp}
                        </span>

                        {isLive ? (
                          <span className="flex items-center gap-[6px] rounded-[8px] bg-[#DC2626] px-[10px] py-[5px]">
                            <span className="size-[7px] rounded-[3.5px] bg-white" />
                            <span className="font-sans text-[11px] font-bold tracking-[0.5px] text-white">
                              LIVE
                            </span>
                          </span>
                        ) : null}
                      </div>
                    </button>
                  </div>
                )
              })}
          </div>
        ) : (
          <span className="py-[40px] text-center font-sans text-[16px] text-[#3E4F3F]">
            Aucune caméra sur cette boutique.
          </span>
        )}

        {pageCount > 1 ? (
          // Pagination (au-delà de 6 caméras) : une pastille par page, la page courante en vert.
          <div className="flex shrink-0 items-center justify-center gap-[10px]">
            {Array.from({ length: pageCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index)}
                aria-label={`Page ${index + 1} sur ${pageCount}`}
                aria-current={index === page}
                className={cn(
                  'size-[12px] rounded-full transition-colors',
                  index === page ? 'bg-[#4F6A47]' : 'bg-[#C7CFC4] hover:bg-[#9FB199]',
                )}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
